import { api, unwrap, unwrapVoid, type Schemas } from '@/lib/api';
import { session, type SessionState, type User } from '@/lib/auth/session';
import { identify, resetTelemetry, track } from '@/lib/telemetry';

/**
 * Auth calls to backend 2.0. Session responses are applied to the in-memory
 * session store here, so screens only navigate.
 */
export type SessionOut = Schemas['SessionOut'];
export type SignupIn = Schemas['SignupIn'];
export type GoogleSignupIn = Schemas['GoogleSignupIn'];
export type GoogleOut = Schemas['GoogleOut'];

function applySession(out: SessionOut): User {
  session.set({
    status: 'signed-in',
    accessToken: out.access_token,
    expiresAt: Date.now() + out.expires_in * 1000,
    user: out.user,
    orgId: out.user.organization_id,
    roleId: null,
  });
  identify(out.user.user_id);
  return out.user;
}

let bootstrapping: Promise<SessionState> | null = null;

/**
 * Resolve `unknown` into `anonymous` or `signed-in` once per page load:
 * refresh from the cookie, then load the user. Routes await this in beforeLoad.
 */
export function ensureSession(): Promise<SessionState> {
  const current = session.get();
  if (current.status !== 'unknown') return Promise.resolve(current);
  bootstrapping ??= (async () => {
    const token = await session.refresh();
    if (!token) {
      session.clear();
      return session.get();
    }
    try {
      const me = unwrap(await api.GET('/api/v2/auth/me'));
      session.set({ status: 'signed-in', user: me.user, orgId: me.user.organization_id });
      identify(me.user.user_id);
    } catch {
      session.clear();
    }
    return session.get();
  })().finally(() => {
    bootstrapping = null;
  });
  return bootstrapping;
}

export async function signIn(email: string, password: string): Promise<User> {
  const out = unwrap(await api.POST('/api/v2/auth/login', { body: { email, password } }));
  track('auth.sign_in', { method: 'password' });
  return applySession(out);
}

export async function signUp(payload: SignupIn): Promise<Schemas['SignupOut']> {
  const out = unwrap(await api.POST('/api/v2/auth/signup', { body: payload }));
  track('auth.sign_up', { resumed: out.resumed });
  return out;
}

export async function sendOtp(email: string): Promise<Schemas['OtpSendOut']> {
  return unwrap(await api.POST('/api/v2/auth/otp/send', { body: { email } }));
}

export async function verifyOtp(email: string, code: string): Promise<User> {
  const out = unwrap(await api.POST('/api/v2/auth/otp/verify', { body: { email, code } }));
  track('auth.email_verified');
  return applySession(out);
}

/** Without `signup`, an unknown Google account answers `signup_required` plus a profile to prefill. */
export async function googleSignIn(idToken: string, signup?: GoogleSignupIn): Promise<GoogleOut> {
  const out = unwrap(
    await api.POST('/api/v2/auth/google', {
      body: signup ? { id_token: idToken, signup } : { id_token: idToken },
    }),
  );
  if (out.status === 'signed_in' && out.session) {
    track('auth.sign_in', { method: 'google', signup: Boolean(signup) });
    applySession(out.session);
  }
  return out;
}

/** Always resolves: the backend answers 202 whether or not the email has an account. */
export async function forgotPassword(email: string): Promise<void> {
  unwrapVoid(await api.POST('/api/v2/auth/password/forgot', { body: { email } }));
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  unwrapVoid(await api.POST('/api/v2/auth/password/reset', { body: { token, new_password: newPassword } }));
}

export async function signOut(): Promise<void> {
  try {
    await api.POST('/api/v2/auth/logout');
  } catch {
    /* the server session may already be gone; the client state is cleared either way */
  }
  session.clear();
  resetTelemetry();
  track('auth.sign_out');
}

/* Google sign-up hand-off: SignInScreen gets `signup_required`, SignUpScreen finishes it. In memory only. */
export interface PendingGoogle {
  idToken: string;
  profile: Schemas['GoogleProfile'];
}
let pendingGoogle: PendingGoogle | null = null;
export function setPendingGoogle(p: PendingGoogle | null): void {
  pendingGoogle = p;
}
export function getPendingGoogle(): PendingGoogle | null {
  return pendingGoogle;
}
